// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import Channels from '@app/features/channel/state/Channels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import type {Guild} from '@app/features/guild/models/Guild';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import Favorites from '@app/features/messaging/state/Favorites';
import SelectedGuild from '@app/features/navigation/state/SelectedGuild';
import ReadStates from '@app/features/read_state/state/ReadStates';
import Relationships from '@app/features/relationship/state/Relationships';
import type {
	CandidateSets,
	GroupDMCandidate,
	GuildCandidate,
	QuickActionCandidate,
	SettingsCandidate,
	TextChannelCandidate,
	UserCandidate,
	VirtualGuildCandidate,
	VoiceChannelCandidate,
} from '@app/features/search/state/QuickSwitcherTypes';
import * as ThemePreferenceCommands from '@app/features/theme/commands/ThemePreferenceCommands';
import Theme from '@app/features/theme/state/Theme';
import {getSettingsSubtabs, getSettingsTabs} from '@app/features/user/components/settings_utils/SettingsConstants';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {FAVORITES_GUILD_ID} from '@app/vendor/constants/AppConstants';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import {QuickSwitcherResultTypes} from '@app/vendor/constants/QuickSwitcherConstants';
import {RelationshipTypes, ThemeTypes} from '@app/vendor/constants/UserConstants';
import {DAYS_PER_WEEK, MS_PER_DAY} from '@app/vendor/date_utils/DateConstants';
import * as SnowflakeUtils from '@app/vendor/snowflake/SnowflakeUtils';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const UNREAD_SORT_WEIGHT_BOOST = DAYS_PER_WEEK * MS_PER_DAY;
function getChannelRecency(channel: {id: string; lastMessageId: string | null}): number {
	if (channel.lastMessageId) {
		return SnowflakeUtils.extractTimestamp(channel.lastMessageId);
	}
	return SnowflakeUtils.extractTimestamp(channel.id);
}
function getChannelSortWeight(channelId: string, baseWeight: number): number {
	const unreadCount = ReadStates.getUnreadCount(channelId);
	const mentionCount = ReadStates.getMentionCount(channelId);
	const hasUnread = unreadCount > 0 || mentionCount > 0;
	return hasUnread ? baseWeight + UNREAD_SORT_WEIGHT_BOOST : baseWeight;
}
export function buildCandidateSets(i18n: I18n): CandidateSets {
	const guilds = Guilds.getGuilds();
	const guildMap = new Map<string, Guild>(guilds.map((guild) => [guild.id, guild]));
	const userCandidates = new Map<string, UserCandidate>();
	const userByChannelId = new Map<string, UserCandidate>();
	const groupDMCandidates: Array<GroupDMCandidate> = [];
	const groupDMByChannelId = new Map<string, GroupDMCandidate>();
	const textChannelCandidates: Array<TextChannelCandidate> = [];
	const voiceChannelCandidates: Array<VoiceChannelCandidate> = [];
	const channelById = new Map<string, TextChannelCandidate | VoiceChannelCandidate>();
	const currentUserId = Users.getCurrentUser()?.id ?? null;
	for (const channel of Channels.allChannels) {
		switch (channel.type) {
			case ChannelTypes.DM:
			case ChannelTypes.DM_PERSONAL_NOTES: {
				const recipientId =
					channel.recipientIds.find((recipientId) => recipientId !== currentUserId) ?? channel.recipientIds.at(0);
				if (!recipientId) break;
				const user = Users.getUser(recipientId);
				if (!user) break;
				const title = ChannelUtils.getDMDisplayName(channel);
				const subtitle = user.tag;
				const searchValues = [title, subtitle, user.username, user.id].filter(Boolean);
				const baseWeight = getChannelRecency(channel);
				const sortWeight = getChannelSortWeight(channel.id, baseWeight);
				const existing = userCandidates.get(user.id);
				const candidate: UserCandidate = {
					type: QuickSwitcherResultTypes.USER,
					id: user.id,
					title,
					subtitle,
					user,
					dmChannelId: channel.id,
					searchValues,
					sortWeight,
				};
				if (!existing || existing.sortWeight < candidate.sortWeight) {
					userCandidates.set(user.id, candidate);
				} else if (existing.dmChannelId == null) {
					userCandidates.set(user.id, {
						...existing,
						dmChannelId: channel.id,
						sortWeight: Math.max(existing.sortWeight, sortWeight),
					});
				}
				const resolvedCandidate = userCandidates.get(user.id);
				if (resolvedCandidate) {
					userByChannelId.set(channel.id, resolvedCandidate);
				}
				break;
			}
			case ChannelTypes.GROUP_DM: {
				const title = ChannelUtils.getDMDisplayName(channel);
				const participantNames = channel.recipientIds
					.map((recipientId) => {
						const user = Users.getUser(recipientId);
						return user ? NicknameUtils.getNickname(user) : null;
					})
					.filter(Boolean) as Array<string>;
				const subtitle = participantNames.length > 0 ? participantNames.join(', ') : i18n._(msg`Group message`);
				const searchValues = [title, ...participantNames];
				const baseWeight = getChannelRecency(channel);
				const sortWeight = getChannelSortWeight(channel.id, baseWeight);
				const candidate: GroupDMCandidate = {
					type: QuickSwitcherResultTypes.GROUP_DM,
					id: channel.id,
					title,
					subtitle,
					channel,
					searchValues,
					sortWeight,
				};
				groupDMCandidates.push(candidate);
				groupDMByChannelId.set(channel.id, candidate);
				break;
			}
			case ChannelTypes.GUILD_TEXT: {
				if (!channel.guildId) break;
				const guild = guildMap.get(channel.guildId) ?? null;
				const title = channel.name ? channel.name : i18n._(msg`Unknown Channel`);
				const subtitle = guild?.name;
				const searchValues = [
					channel.name ?? '',
					channel.topic ?? '',
					guild?.name ?? '',
					channel.parentId ?? '',
				].filter(Boolean);
				const baseWeight = getChannelRecency(channel);
				const sortWeight = getChannelSortWeight(channel.id, baseWeight);
				const candidate: TextChannelCandidate = {
					type: QuickSwitcherResultTypes.TEXT_CHANNEL,
					id: channel.id,
					title,
					subtitle,
					channel,
					guild,
					searchValues,
					sortWeight,
				};
				textChannelCandidates.push(candidate);
				channelById.set(channel.id, candidate);
				break;
			}
			case ChannelTypes.GUILD_VOICE: {
				if (!channel.guildId) break;
				const guild = guildMap.get(channel.guildId) ?? null;
				const title = channel.name ?? i18n._(msg`Voice Channel`);
				const subtitle = guild?.name;
				const searchValues = [channel.name ?? '', guild?.name ?? ''].filter(Boolean);
				const baseWeight = getChannelRecency(channel);
				const sortWeight = getChannelSortWeight(channel.id, baseWeight);
				const candidate: VoiceChannelCandidate = {
					type: QuickSwitcherResultTypes.VOICE_CHANNEL,
					id: channel.id,
					title,
					subtitle,
					channel,
					guild,
					searchValues,
					sortWeight,
				};
				voiceChannelCandidates.push(candidate);
				channelById.set(channel.id, candidate);
				break;
			}
			default:
				break;
		}
	}
	for (const relationship of Relationships.getRelationships()) {
		if (relationship.type !== RelationshipTypes.FRIEND) {
			continue;
		}
		const user = relationship.user;
		if (!userCandidates.has(user.id)) {
			const title = NicknameUtils.getNickname(user);
			const subtitle = user.tag;
			const searchValues = [title, subtitle, user.username, user.id].filter(Boolean);
			userCandidates.set(user.id, {
				type: QuickSwitcherResultTypes.USER,
				id: user.id,
				title,
				subtitle,
				user,
				dmChannelId: null,
				searchValues,
				sortWeight: relationship.since.getTime(),
			});
		}
	}
	for (const user of Users.getUsers()) {
		if (user.id === currentUserId) continue;
		if (!userCandidates.has(user.id)) {
			const title = NicknameUtils.getNickname(user);
			const subtitle = user.tag;
			const searchValues = [title, subtitle, user.username, user.id].filter(Boolean);
			userCandidates.set(user.id, {
				type: QuickSwitcherResultTypes.USER,
				id: user.id,
				title,
				subtitle,
				user,
				dmChannelId: null,
				searchValues,
				sortWeight: SnowflakeUtils.extractTimestamp(user.id),
			});
		}
	}
	const selectedGuildId = SelectedGuild.selectedGuildId;
	if (selectedGuildId) {
		const guildMembers = GuildMembers.getMembers(selectedGuildId);
		for (const member of guildMembers) {
			if (member.user.id === currentUserId) continue;
			if (!userCandidates.has(member.user.id)) {
				const title = member.nick ?? NicknameUtils.getNickname(member.user);
				const subtitle = member.user.tag;
				const searchValues = [title, subtitle, member.user.username, member.user.id, member.nick].filter(
					Boolean,
				) as Array<string>;
				userCandidates.set(member.user.id, {
					type: QuickSwitcherResultTypes.USER,
					id: member.user.id,
					title,
					subtitle,
					user: member.user,
					dmChannelId: null,
					searchValues,
					sortWeight: member.joinedAt ? new Date(member.joinedAt).getTime() : 0,
				});
			}
		}
	}
	const guildCandidates: Array<GuildCandidate> = guilds.map((guild) => ({
		type: QuickSwitcherResultTypes.GUILD,
		id: guild.id,
		title: guild.name,
		subtitle: undefined,
		guild,
		searchValues: [guild.name, guild.vanityURLCode ?? '', guild.id].filter(Boolean),
		sortWeight: guild.joinedAt ? new Date(guild.joinedAt).getTime() : 0,
	}));
	const virtualGuildCandidates: Array<VirtualGuildCandidate> = [];
	virtualGuildCandidates.push({
		type: QuickSwitcherResultTypes.VIRTUAL_GUILD,
		id: 'home',
		title: i18n._(msg`Home`),
		subtitle: i18n._(msg`Direct Messages`),
		virtualGuildType: 'home',
		searchValues: ['Home', 'DM', 'DMs', 'Direct Messages', 'Messages'],
		sortWeight: Date.now(),
	});
	if (Favorites.hasAnyFavorites) {
		virtualGuildCandidates.push({
			type: QuickSwitcherResultTypes.VIRTUAL_GUILD,
			id: 'favorites',
			title: i18n._(msg`Favorites`),
			subtitle: undefined,
			virtualGuildType: 'favorites',
			searchValues: ['Favorites', 'Fav', 'Starred', FAVORITES_GUILD_ID],
			sortWeight: Date.now(),
		});
	}
	const settingsCandidates: Array<SettingsCandidate> = [];
	const hasExpressionPackAccess = Users.getCurrentUser()?.isStaff() ?? false;
	const accessibleTabs = getSettingsTabs(i18n).filter((tab) => {
		if (!hasExpressionPackAccess && tab.type === 'expression_packs') {
			return false;
		}
		return true;
	});
	for (const tab of accessibleTabs) {
		settingsCandidates.push({
			type: QuickSwitcherResultTypes.SETTINGS,
			id: tab.type,
			title: tab.label,
			subtitle: undefined,
			settingsTab: tab,
			settingsSubtab: undefined,
			searchValues: [tab.label, 'settings', 'preferences', tab.type],
			sortWeight: 0,
		});
	}
	for (const subtab of getSettingsSubtabs(i18n)) {
		const parentTab = accessibleTabs.find((t) => t.type === subtab.parentTab);
		if (!parentTab) continue;
		settingsCandidates.push({
			type: QuickSwitcherResultTypes.SETTINGS,
			id: `${subtab.parentTab}_${subtab.type}`,
			title: subtab.label,
			subtitle: parentTab.label,
			settingsTab: parentTab,
			settingsSubtab: subtab,
			searchValues: [subtab.label, parentTab.label, 'settings', subtab.type],
			sortWeight: 0,
		});
	}
	const quickActionCandidates: Array<QuickActionCandidate> = [];
	quickActionCandidates.push({
		type: QuickSwitcherResultTypes.QUICK_ACTION,
		id: 'toggle_theme',
		title: i18n._(msg`Toggle Theme`),
		subtitle: i18n._(msg`Switch between Light and Dark mode`),
		action: () => {
			const currentTheme = Theme.effectiveTheme;
			const newTheme = currentTheme === ThemeTypes.DARK ? ThemeTypes.LIGHT : ThemeTypes.DARK;
			ThemePreferenceCommands.updateThemePreference(newTheme);
		},
		searchValues: ['theme', 'light', 'dark', 'mode', 'switch', 'toggle'],
		sortWeight: 0,
	});
	quickActionCandidates.push({
		type: QuickSwitcherResultTypes.QUICK_ACTION,
		id: 'toggle_compact_mode',
		title: i18n._(msg`Toggle Compact Mode`),
		subtitle: UserSettings.getMessageDisplayCompact()
			? i18n._(msg`Disable Compact Mode`)
			: i18n._(msg`Enable Compact Mode`),
		action: () => {
			UserSettings.saveSettings({
				messageDisplayCompact: !UserSettings.getMessageDisplayCompact(),
			});
		},
		searchValues: ['compact', 'mode', 'display', 'message', 'toggle'],
		sortWeight: 0,
	});
	quickActionCandidates.push({
		type: QuickSwitcherResultTypes.QUICK_ACTION,
		id: 'toggle_reduced_motion',
		title: i18n._(msg`Toggle Reduced Motion`),
		subtitle: Accessibility.useReducedMotion ? i18n._(msg`Disable Reduced Motion`) : i18n._(msg`Enable Reduced Motion`),
		action: () => {
			Accessibility.updateSettings({
				reducedMotionOverride: !Accessibility.useReducedMotion,
				syncReducedMotionWithSystem: false,
			});
		},
		searchValues: ['reduced', 'motion', 'animation', 'toggle'],
		sortWeight: 0,
	});
	return {
		users: Array.from(userCandidates.values()),
		userByChannelId,
		groupDMs: groupDMCandidates,
		groupDMByChannelId,
		textChannels: textChannelCandidates,
		voiceChannels: voiceChannelCandidates,
		guilds: guildCandidates,
		virtualGuilds: virtualGuildCandidates,
		settings: settingsCandidates,
		quickActions: quickActionCandidates,
		channelById,
	};
}
